import { Box, Flex, Grid, Image, Text } from '@chakra-ui/react';
import { useRef, useState } from 'react';
import Slider from 'react-slick';
import { GoChevronLeft, GoChevronRight } from 'react-icons/go';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import StructuredTextParser from '../../app/landing_pages/karma/Components/StructuredTextParser';
import { render } from 'datocms-structured-text-to-html-string';

const HighlightsSection = ({ sectionContent }) => {
  const sliderRef = useRef(null);
  const cards = sectionContent?.[1]?.cards;
  const cardsList = cards;
  const contentLength = cardsList?.length >= 3;

  const [current, setCurrent] = useState(0);

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    gap: '16px',
    beforeChange: (_, next) => setCurrent(next),
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 640, settings: { slidesToShow: 1 } },
    ],
  };

  const progress = ((current + 3) * 100) / cardsList.length;
  const notHasPrevCard = current === 0;
  const notHasNextCard = current >= cardsList.length - settings.slidesToShow;

  const getCards = () => {
    return cardsList?.map(card => (
      <Box key={card.id} px={{ base: '0px', md: "0px" }}>
        <Box bg="#f6f0e8" overflow="hidden">
          <Image
            src={card?.thumbnail?.url}
            alt={card?.thumbnail?.alt || 'image'}
            w={{ base: '100%', md: 'auto' }}
            h={{ base: '100%', md: 'auto' }}
            objectFit="cover"
            loading="lazy"
          />
          <Flex
            mt={{ base: '10px', md: '24px' }}
            borderLeft={'2px solid #CF4520'}
            px={'8px'}
            alignItems={'center'}
            rounded={'0'}
            gap={'8px'}
            flexWrap={'wrap'}
          >
            <Image
              src={card?.mobileImage?.url}
              alt={card?.mobileImage?.alt || 'image'}
              w={{ base: '16px', md: '24px' }}
              h={{ base: '16px', md: '24px' }}
              objectFit="cover"
              loading="lazy"
            />
            <Text
              as={'span'}
              color="#28231E"
              fontFamily="FedraSansStd-medium"
              fontSize={{ base: '18px', md: '24px', lg: '24px' }}
              lineHeight={{ base: '12.8px', md: '20px', lg: '20px' }}
              letterSpacing={{ base: '2.24px', md: '3.2px', lg: '3.2px' }}
              textTransform="uppercase"
              pb="5px"
              className="allSmallCase"
            >
              {card?.buttonText}
            </Text>
            <Text
              as={'span'}
              color={'#1D1A16'}
              fontSize={{ base: '14px', md: '20px', lg: '20px' }}
              lineHeight={{ base: '16.8px', md: '25px', lg: '25px' }}
              fontWeight={'400'}
              letterSpacing={{ base: '0.28px', md: '0.4px', lg: '0.4px' }}
              fontFamily="'FedraSansStd-book', sans-serif"
              flexWrap={'wrap'}
            >
              {card?.tags}
            </Text>
          </Flex>
          <StructuredTextParser
            str={render(card?.description)}
            sectionContent={card}
            color="rgba(29, 26, 22, 0.70)"
            fontFamily="FedraSansStd-book"
            fontSize={{ base: '16px', md: '24px', lg: '24px' }}
            lineHeight={{ base: '22.4px', md: '33.6px', lg: '33.6px' }}
            letterSpacing={{ base: '0.32px', md: '0.48px', lg: '0.48px' }}
            pt={{ base: '10px', md: '18px', lg: '18px' }}
            mt="0px"
          />
        </Box>
      </Box>
    ));
  };

  return (
    <Box background={'#F8F1E7'}>
      <Box
        position="relative"
        pt={{ base: '48px', md: "120px", lg: '160px' }}
        pb={{ base: '48px', md: "96px", lg: '136px' }}
        maxWidth={{ base: '100%', md: '1202px' }}
        mx={'auto'}
        px={{ base: '16px', md: '16px' }}
      >
        <Text
          color="#28231E"
          fontFamily="FedraSansStd-book"
          fontSize={{ base: '24px', md: '32px', lg: '32px' }}
          lineHeight={{ base: '24px', md: '32px', lg: '32px' }}
          letterSpacing={{ base: '4.2px', md: '7.12px', lg: '7.12px' }}
          fontWeight="400"
          textTransform="uppercase"
          className="allSmallCase"
          pb={"8px"}
        >
          {sectionContent?.[0]?.titleText}
        </Text>
        <Text
          color="#2F2F2F"
          fontFamily="FedraSerifDisplayRegular"
          fontSize={{ base: '40px', md: '72px', lg: '72px' }}
          lineHeight={{ base: '40px', md: '72px', lg: '72px' }}
          letterSpacing={{ base: '-0.8px', md: '-1.44px', lg: '-1.44px' }}
          fontWeight="400"
          pb={{ base: '32px', md: '31.48px', lg: '31.48px' }}
        >
          {sectionContent?.[0]?.titleSubtext}
        </Text>

        {contentLength ? (
          <Slider {...settings} ref={sliderRef}>
            {getCards()}
          </Slider>
        ) : (
          <Grid
            templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)' }}
            gap={{ base: '32px', md: '32px' }}
            w={'100%'}
            alignItems={'flex-start'}
          >
            {getCards()}
          </Grid>
        )}
        {contentLength && (
          <Box
            w={'100%'}
            display={'flex'}
            alignItems={'center'}
            gap={{ base: '10px', md: '16px' }}
          >
            <Box
              w={'100%'}
              h="2px"
              background="rgba(40, 35, 30, 0.10)"
              position="relative"
              borderRadius={'full'}
              mt={'0px'}
            >
              <Box
                position="absolute"
                top={0}
                left={0}
                h="2px"
                background="#CF4520"
                transition="width 0.5s"
                width={`${progress}%`}
                borderRadius={'full'}
              />
            </Box>
            <Flex gap={{ base: '10px', md: '16px' }}>
              <Box
                w={{ base: '48px', md: '48px' }}
                h={{ base: '48px', md: '48px' }}
                borderRadius={'50%'}
                boxShadow={
                  '0 25px 7px 0 rgba(0, 0, 0, 0.00), 0 16px 6px 0 rgba(0, 0, 0, 0.01), 0 9px 5px 0 rgba(0, 0, 0, 0.02), 0 4px 4px 0 rgba(0, 0, 0, 0.03), 0 1px 2px 0 rgba(0, 0, 0, 0.03)'
                }
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                cursor={notHasPrevCard ? 'not-allowed' : 'pointer'}
                background={notHasPrevCard ? 'rgba(40, 35, 30, 0.20)' : 'white'}
                onClick={() =>
                  !notHasPrevCard && sliderRef.current?.slickPrev()
                }
              >
                <GoChevronLeft
                  size={28}
                  color={notHasPrevCard ? 'rgba(40, 35, 30, 0.40)' : '#CF4520'}
                />
              </Box>
              <Box
                w={{ base: '48px', md: '48px' }}
                h={{ base: '48px', md: '48px' }}
                borderRadius={'50%'}
                boxShadow={
                  '0 25px 7px 0 rgba(0, 0, 0, 0.00), 0 16px 6px 0 rgba(0, 0, 0, 0.01), 0 9px 5px 0 rgba(0, 0, 0, 0.02), 0 4px 4px 0 rgba(0, 0, 0, 0.03), 0 1px 2px 0 rgba(0, 0, 0, 0.03)'
                }
                display={'flex'}
                alignItems={'center'}
                justifyContent={'center'}
                cursor={notHasNextCard ? 'not-allowed' : 'pointer'}
                background={notHasNextCard ? 'rgba(40, 35, 30, 0.20)' : 'white'}
                onClick={() =>
                  !notHasNextCard && sliderRef.current?.slickNext()
                }
              >
                <GoChevronRight
                  size={28}
                  color={notHasNextCard ? 'rgba(40, 35, 30, 0.40)' : '#CF4520'}
                />
              </Box>
            </Flex>
          </Box>
        )}
      </Box>
      <style jsx global>{`
        .allSmallCase {
          font-variant: all-small-caps;
        }
      `}</style>
    </Box>
  );
};

export default HighlightsSection;
